"use client";
import React from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  MotionValue,
  AnimatePresence,
} from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { Sparkles } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useAppSelector } from "@/store/hooks";

// --- Background Components ---
const BackgroundElements = ({ mode }: { mode: "light" | "dark" }) => {
  const stars = useMemo(
    () =>
      [...Array(20)].map((_, i) => ({
        id: i,
        top: `${((((i * 47 + 13) * 9301 + 49297) % 233280) / 233280) * 100}%`,
        left: `${((((i * 97 + 31) * 9301 + 49297) % 233280) / 233280) * 100}%`,
        width: `${((((i * 53 + 7) * 9301 + 49297) % 233280) / 233280) * 2 + 1}px`,
        height: `${((((i * 73 + 19) * 9301 + 49297) % 233280) / 233280) * 2 + 1}px`,
      })),
    [],
  );

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const activeMode = mounted ? mode : "light";

  return (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none -z-10 transition-colors duration-700 ${activeMode === "dark" ? "bg-[#0B0F19]" : "bg-slate-50"}`}
    >
      {/* Base Gradient Ambience */}
      <div
        className={`absolute top-0 left-0 w-full h-[50vh] blur-[120px] transition-opacity duration-700 ${activeMode === "dark" ? "bg-[#8B31FF]/10 opacity-100" : "bg-[#8B31FF]/5 opacity-50"}`}
      />
      <div
        className={`absolute bottom-0 right-0 w-full h-[50vh] blur-[120px] transition-opacity duration-700 ${activeMode === "dark" ? "bg-[#8B31FF]/10 opacity-100" : "bg-[#8B31FF]/5 opacity-50"}`}
      />

      {/* Grid Pattern */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ${activeMode === "dark" ? "opacity-[0.03]" : "opacity-[0.05]"}`}
        style={{
          backgroundImage:
            activeMode === "dark"
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
          backgroundSize: "50px 50px",
        }}
      />

      {/* Floating Orbs */}
      <motion.div
        className={`absolute top-[10%] left-[10%] w-[30vw] h-[30vw] bg-[#8B31FF] rounded-full mix-blend-screen transition-opacity duration-700 filter blur-[100px] ${activeMode === "dark" ? "opacity-20" : "opacity-10"}`}
        animate={{
          y: [0, 50, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className={`absolute top-[40%] right-[10%] w-[40vw] h-[40vw] bg-[#872ffb] rounded-full mix-blend-screen transition-opacity duration-700 filter blur-[120px] ${activeMode === "dark" ? "opacity-10" : "opacity-5"}`}
        animate={{
          y: [0, -50, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Stars - Only show in dark mode */}
      {activeMode === "dark" &&
        stars.map((star) => (
          <div
            key={star.id}
            className="absolute bg-white rounded-full opacity-20"
            style={{
              top: star.top,
              left: star.left,
              width: star.width,
              height: star.height,
            }}
          />
        ))}
    </div>
  );
};

export interface HeroSlide {
  badge: string;
  title: string;
  highlight: string;
  desc: string;
  cta1: string;
  cta2: string;
}

export const HeroParallax = ({
  products,
  slides,
}: {
  products: {
    id: string;
    title: string;
    link: string;
    thumbnail: string;
  }[];
  slides?: HeroSlide[];
}) => {
  const firstRow = products.slice(0, 5);
  const secondRow = products.slice(5, 10);
  const thirdRow = products.slice(10, 15);
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const springConfig = { stiffness: 120, damping: 26, mass: 0.6 };

  const translateX = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, 600]),
    springConfig,
  );
  const translateXReverse = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, -600]),
    springConfig,
  );
  const rotateX = useSpring(
    useTransform(scrollYProgress, [0, 0.4], [12, 0]),
    springConfig,
  );
  const opacity = useSpring(
    useTransform(scrollYProgress, [0, 0.35], [0.2, 1]),
    springConfig,
  );
  const rotateZ = useSpring(
    useTransform(scrollYProgress, [0, 0.4], [8, 0]),
    springConfig,
  );
  const translateY = useSpring(
    useTransform(scrollYProgress, [0, 0.4], [-400, 50]),
    springConfig,
  );
  const { mode } = useAppSelector((state) => state.theme);

  return (
    <div
      ref={ref}
      className="w-full pt-12 sm:pt-16 md:pt-20 pb-20 sm:pb-28 overflow-hidden antialiased relative flex flex-col self-auto [perspective:1000px] [transform-style:preserve-3d]"
    >
      <BackgroundElements mode={mode} />

      <Header mode={mode} slides={slides} />
      <motion.div
        style={{
          rotateX,
          rotateZ,
          translateY,
          opacity,
        }}
        className="transform-gpu will-change-transform"
      >
        <motion.div className="flex flex-row-reverse gap-6 sm:gap-12 md:gap-16 mb-8 sm:mb-12 md:mb-16 transform-gpu will-change-transform">
          {firstRow.map((product, index) => (
            <ProductCard
              product={product}
              translate={translateX}
              key={product.id}
              mode={mode}
              priority={index >= firstRow.length - 2}
            />
          ))}
        </motion.div>
        <motion.div className="flex flex-row gap-6 sm:gap-12 md:gap-16 mb-8 sm:mb-12 md:mb-16 transform-gpu will-change-transform">
          {secondRow.map((product) => (
            <ProductCard
              product={product}
              translate={translateXReverse}
              key={product.id}
              mode={mode}
            />
          ))}
        </motion.div>
        <motion.div className="flex flex-row-reverse gap-6 sm:gap-12 md:gap-16 transform-gpu will-change-transform">
          {thirdRow.map((product) => (
            <ProductCard
              product={product}
              translate={translateX}
              key={product.id}
              mode={mode}
            />
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

const fallbackSlides: HeroSlide[] = [
  {
    badge: "Creative Excellence",
    title: "The Ultimate Development Studio",
    highlight: "",
    desc: "We build beautiful products with the latest technologies and frameworks. We are a team of passionate developers and designers that love to build amazing products.",
    cta1: "Get Started",
    cta2: "View Portfolio",
  },
  {
    badge: "Next-Gen Technology",
    title: "Innovating the Future of Tech",
    highlight: "",
    desc: "Transforming ideas into reality with cutting-edge solutions. Our expertise spans across full-stack development, AI integration, and cloud architecture.",
    cta1: "Our Services",
    cta2: "Contact Us",
  },
  {
    badge: "Business Growth",
    title: "Scalable Solutions for Growth",
    highlight: "",
    desc: "Empowering businesses with robust and scalable software. From startups to enterprises, we deliver code that stands the test of time and traffic.",
    cta1: "Start Scaling",
    cta2: "Learn More",
  },
];

export const Header = ({
  mode,
  slides: slidesProp,
}: {
  mode: "light" | "dark";
  slides?: HeroSlide[];
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const slides =
    slidesProp && slidesProp.length > 0 ? slidesProp : fallbackSlides;

  // Reset index when slides array changes (e.g. API data loads)
  useEffect(() => {
    setCurrentSlide(0);
  }, [slides.length]);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [slides.length, isPaused]);

  const safeIndex = currentSlide < slides.length ? currentSlide : 0;

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className="max-w-7xl relative mx-auto pt-6 sm:pt-10 md:pt-14 pb-8 sm:pb-12 md:pb-16 px-4 sm:px-6 lg:px-8 w-full left-0 top-0 min-h-[calc(100dvh-48px)] sm:min-h-[calc(100dvh-64px)] md:min-h-[calc(100dvh-80px)] flex flex-col justify-center pointer-events-none z-30"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={safeIndex}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -14 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-20"
        >
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border transition-all duration-300 ${
              mode === "dark"
                ? "border-[#8B31FF]/30 bg-[#8B31FF]/15 shadow-[0_0_15px_rgba(79,17,194,0.3)]"
                : "border-[#8B31FF]/20 bg-white/70 shadow-sm"
            } mb-4 sm:mb-5 backdrop-blur-md`}
          >
            <Sparkles className="w-3.5 h-3.5 text-[#872ffb] animate-pulse" />
            <span
              className={`text-xs font-bold tracking-wider uppercase transition-colors duration-300 ${mode === "dark" ? "text-[#872ffb]" : "text-[#8B31FF]"}`}
            >
              {slides[safeIndex].badge}
            </span>
          </div>

          {/* Responsive Heading */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.12]">
            {slides[safeIndex].title.split(" ").map((word, i) => (
              <span
                key={i}
                className={`inline-block mr-2 sm:mr-3 transition-colors duration-300 ${
                  i % 2 === 0
                    ? mode === "dark"
                      ? "text-white"
                      : "text-gray-900"
                    : "text-[#872ffb] drop-shadow-[0_0_15px_rgba(135,47,251,0.4)]"
                }`}
              >
                {word}
              </span>
            ))}
            {slides[safeIndex].highlight && (
              <span className="inline-block mr-2 sm:mr-3 text-[#872ffb] drop-shadow-[0_0_15px_rgba(135,47,251,0.4)]">
                {slides[safeIndex].highlight}
              </span>
            )}
          </h1>

          {/* Subtitle */}
          <p
            className={`max-w-2xl text-sm sm:text-base md:text-lg leading-relaxed drop-shadow-sm mb-6 sm:mb-8 transition-colors duration-300 ${mode === "dark" ? "text-gray-300" : "text-gray-600"}`}
          >
            {slides[safeIndex].desc}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 pointer-events-auto">
            <Link href="/contact">
              <button className="px-6 sm:px-8 py-3 sm:py-3.5 cursor-pointer rounded-lg bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white text-sm sm:text-base font-semibold shadow-[0_0_20px_rgba(79,17,194,0.4)] hover:shadow-[0_0_30px_rgba(79,17,194,0.65)] hover:scale-[1.03] active:scale-[0.98] transition-all duration-300">
                {slides[safeIndex].cta1}
              </button>
            </Link>
            <Link href="/portfolio">
              <button
                className={`px-6 sm:px-8 py-3 sm:py-3.5 cursor-pointer rounded-lg border text-sm sm:text-base transition-all duration-300 font-semibold hover:scale-[1.03] active:scale-[0.98] ${
                  mode === "dark"
                    ? "border-white/20 bg-white/5 text-white hover:bg-white/10 hover:border-[#872ffb]/50 hover:text-[#872ffb]"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-[#8B31FF]/50 hover:text-[#8B31FF]"
                } backdrop-blur-sm shadow-sm`}
              >
                {slides[safeIndex].cta2}
              </button>
            </Link>
          </div>

          {/* Slide Pagination Dots */}
          {slides.length > 1 && (
            <div className="flex items-center gap-2 mt-6 sm:mt-8 pointer-events-auto">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  className={`cursor-pointer transition-all duration-300 rounded-full ${
                    index === safeIndex
                      ? "w-8 h-2 bg-gradient-to-r from-[#8B31FF] to-[#872ffb]"
                      : mode === "dark"
                        ? "w-2 h-2 bg-white/20 hover:bg-white/40"
                        : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export const ProductCard = ({
  product,
  translate,
  mode,
  priority = false,
}: {
  product: {
    title: string;
    link: string;
    thumbnail: string;
  };
  translate: MotionValue<number>;
  mode: "light" | "dark";
  priority?: boolean;
}) => {
  return (
    <motion.div
      style={{
        x: translate,
      }}
      whileHover={{
        y: -14,
      }}
      key={product.title}
      className={`group/product w-72 sm:w-96 md:w-[26rem] lg:w-[30rem] relative flex-shrink-0 flex flex-col gap-4 transform-gpu will-change-transform`}
    >
      <Link
        href={product.link}
        className={`block h-60 sm:h-72 md:h-80 lg:h-96 w-full relative rounded-lg overflow-hidden border transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] ${
          mode === "dark"
            ? "bg-black/40 border-white/10 group-hover/product:border-[#872ffb]/80 group-hover/product:shadow-[0_0_35px_rgba(135,47,251,0.3)]"
            : "bg-white/80 border-gray-200 group-hover/product:border-[#8B31FF]/50 group-hover/product:shadow-[0_12px_35px_rgba(79,17,194,0.15)]"
        }`}
      >
        <Image
          src={product.thumbnail}
          fill
          sizes="(max-width: 640px) 288px, (max-width: 768px) 384px, (max-width: 1024px) 416px, 480px"
          loading={priority ? undefined : "lazy"}
          priority={priority}
          className="object-cover object-left-top transition-transform duration-700 group-hover/product:scale-105"
          alt={product.title}
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 h-full w-full opacity-0 group-hover/product:opacity-30 bg-[#8B31FF] pointer-events-none transition-opacity duration-300 mix-blend-overlay"></div>
      </Link>

      {/* Text UNDER the card */}
      <div className="w-full flex flex-col pointer-events-none px-2">
        <h2
          className={`font-bold text-lg sm:text-xl md:text-2xl transition-colors duration-300 group-hover/product:text-[#872ffb] ${mode === "dark" ? "text-white" : "text-gray-900"}`}
        >
          {product.title}
        </h2>
        <div
          className={`mt-2 h-1 w-10 rounded-full transform origin-left scale-x-0 group-hover/product:scale-x-100 transition-transform duration-300 ${mode === "dark" ? "bg-[#872ffb]" : "bg-[#8B31FF]"}`}
        />
      </div>
    </motion.div>
  );
};
